import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import HFDropdown from 'components/HFInputs/HFDropdown'
import HFCheckbox from 'components/HFInputs/HFCheckbox'
import AlertBox from 'components/AlertBox/AlertBox'
import JustificationField from 'components/JustificationField/JustificationField'
import { useFormContext } from 'react-hook-form'
import { useEffect, useRef, useState } from 'react'
import { useJustifications } from 'hooks/useJustifications'
import { Segment, SuggestType } from 'utils/enums'

import { getProfitSymbol } from '../utils/ProfitSymbol'
import styles from '../../Dialog.module.sass'

const JUSTIFICATION_CATEGORY_PROCESSES = 3

/**
 * Trust AI provider suggestion + provider selection.
 *
 * Owns the selection state (chosen provider, pending provider that requires a
 * justification) and renders the AI suggestion panel, the provider dropdown and
 * the justification field. Reports the final selection to the parent through
 * `onProviderSelected`.
 */
export const ProviderSuggestion = ({
	suggestions,
	isLoading,
	filteredEstablishments,
	processId,
	validateRef,
	onProviderSelected,
	setShowWarning,
	requisitionStateId,
	accidentType,
	showManualProvider = false,
	manualProviderOnly = false,
}) => {
	const { i18n } = useGlobal()
	const { watch, setValue } = useFormContext()

	const [bodyPart, postalCode, isSerious, hasStoppedWorking, isOnSickLeave] =
		watch([
			'bodyPart.bodyPartName',
			'dataValidation.postalCode',
			'accident.isSerious',
			'clinicalAssistance.hasStoppedWorking',
			'clinicalAssistance.isOnSickLeave',
		])

	const providerFieldName = 'provider.establishmentId'
	const currentProvider = watch(providerFieldName)

	const [selectedProviderId, setSelectedProviderId] = useState(null)
	const [pendingProvider, setPendingProvider] = useState(null)
	const [selectedUserJustification, setSelectedUserJustification] =
		useState(null)
	const [selectedUserJustificationId, setSelectedUserJustificationId] =
		useState(null)
	const [showWarning, setShowWarningLocal] = useState(false)
	const effectiveSetShowWarning = setShowWarning || setShowWarningLocal

	const { justifications } = useJustifications(
		JUSTIFICATION_CATEGORY_PROCESSES
	)

	const getSymbol = (number, className) => {
		const icons = {
			0: 'fast-upward-one',
			1: 'fast-upward-two',
			2: 'fast-downward-one',
			3: 'fast-downward-two',
			4: 'equals',
		}
		const labels = {
			0: i18n`Positive`,
			1: i18n`Positive`,
			2: i18n`Negative`,
			3: i18n`Negative`,
			4: i18n`Moderate`,
		}
		const iconName = icons[number]
		if (!iconName) return null
		return (
			<>
				<Icon
					name={iconName}
					style={{ fill: className, marginRight: '5px' }}
					width={number === 4 ? '16px' : '14px'}
					height={number === 4 ? '16px' : '14px'}
				/>
				<p style={{ margin: '0px 5px 0px 0px' }}>{labels[number]}</p>
			</>
		)
	}

	const handleSelectProvider = (
		p,
		skipJustification = false,
		customJustification = null,
		skipLogging = false,
		customJustificationId = null
	) => {
		setValue(providerFieldName, p.providerId.toString())
		setSelectedProviderId(p.providerId)

		if (skipLogging) return

		const isFirstProvider =
			suggestions?.providerReferrals?.[0]?.providerId === p.providerId
		const userJustification =
			isFirstProvider || skipJustification
				? null
				: customJustification || selectedUserJustification

		const justificationFromTrustie =
			suggestions?.description || 'Sugestão aceite'

		onProviderSelected({
			processId: processId,
			requisitionId: null,
			codes: null,
			bodyPartId: bodyPart,
			postalCode: postalCode,
			scheduleDate: null,
			appointmentDate: null,
			providers: suggestions.providerReferrals.map((pr) => ({
				providerId: pr.providerId,
				profitProvider: Math.round(pr.profit || 0),
				distance: pr.distance || 0,
				speed: pr.averageTime || 0,
			})),
			chosenProvider: p.providerId,
			justification: isFirstProvider
				? justificationFromTrustie
				: userJustification || justificationFromTrustie,
			userJustification: userJustification,
			userJustificationId:
				customJustificationId ?? selectedUserJustificationId,
			description: justificationFromTrustie,
			suggestType: SuggestType.ProcessForwarding,
			ita: hasStoppedWorking ?? isOnSickLeave ?? false,
			severe: isSerious ?? false,
		})
	}

	const handleProviderClick = (p) => {
		if (selectedProviderId === p.providerId) {
			setSelectedProviderId(null)
			setValue(providerFieldName, null)
			setPendingProvider(null)
			setSelectedUserJustification(null)
			setSelectedUserJustificationId(null)
			return
		}
		const isFirstProvider =
			suggestions?.providerReferrals?.[0]?.providerId === p.providerId
		if (isFirstProvider) {
			handleSelectProvider(p, true)
			setSelectedUserJustificationId(null)
			setPendingProvider(null)
		} else {
			handleSelectProvider(p, false, null, true)
			setPendingProvider(p)
		}
	}

	const handleJustificationChange = (justification) => {
		if (!justification) {
			setSelectedUserJustification(null)
			setSelectedUserJustificationId(null)
			return
		}
		setSelectedUserJustification(justification.justificationName)
		setSelectedUserJustificationId(justification.justificationId)
		if (pendingProvider) {
			handleSelectProvider(
				pendingProvider,
				false,
				justification.justificationName,
				false,
				justification.justificationId
			)
		}
	}

	const handleJustificationCancel = () => {
		setPendingProvider(null)
		setSelectedUserJustification(null)
		setSelectedUserJustificationId(null)
		setSelectedProviderId(null)
		setValue(providerFieldName, null)
	}

	const handleManualProviderSelect = (selectedValue) => {
		setValue(providerFieldName, selectedValue)

		if (!suggestions?.providerReferrals?.length) {
			return
		}

		const firstSuggestion = suggestions.providerReferrals[0]
		const selectedId = parseInt(selectedValue, 10)

		if (
			firstSuggestion &&
			Number(firstSuggestion.providerId) === selectedId
		) {
			handleSelectProvider(firstSuggestion, true)
			setSelectedUserJustification(null)
			setSelectedUserJustificationId(null)
			setPendingProvider(null)
		} else {
			const selectedProvider = suggestions.providerReferrals.find(
				(p) => Number(p.providerId) === selectedId
			)
			if (selectedProvider) {
				setPendingProvider(selectedProvider)
			} else {
				setPendingProvider({
					providerId: selectedId,
					providerName: 'Manual Selection',
				})
			}
		}
	}

	const didInitFromPrefill = useRef(false)
	useEffect(() => {
		if (didInitFromPrefill.current) return
		if (!currentProvider) return
		if (!suggestions?.providerReferrals?.length) return

		didInitFromPrefill.current = true

		const firstSuggestion = suggestions.providerReferrals[0]
		const prefilledId = parseInt(currentProvider, 10)

		if (Number(firstSuggestion.providerId) === prefilledId) {
			return
		}

		const selectedProvider = suggestions.providerReferrals.find(
			(p) => Number(p.providerId) === prefilledId
		)
		setPendingProvider(
			selectedProvider || {
				providerId: prefilledId,
				providerName: 'Manual Selection',
			}
		)
	}, [currentProvider, suggestions])

	// Clear the selected provider when the underlying form value changes.
	useEffect(() => {
		if (
			selectedProviderId !== null &&
			currentProvider !== selectedProviderId?.toString()
		) {
			setSelectedProviderId(null)
		}
	}, [currentProvider])

	useEffect(() => {
		setShowWarningLocal(false)
	}, [pendingProvider])

	// A pending provider requires a justification before the task can proceed.
	useEffect(() => {
		if (validateRef) {
			validateRef.current = () => {
				if (pendingProvider && !selectedUserJustification) {
					effectiveSetShowWarning(true)
					return false
				}
				return true
			}
		}
	}, [
		pendingProvider,
		selectedUserJustification,
		validateRef,
		effectiveSetShowWarning,
	])

	return (
		<>
			{requisitionStateId !== 2 &&
			((accidentType === Segment.WorkRelated &&
				(isLoading || suggestions)) ||
				showManualProvider) ? (
				<>
					{!manualProviderOnly &&
						accidentType === Segment.WorkRelated &&
						(isLoading || suggestions) && (
							<fieldset>
								<VStack>
									<HStack>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											viewBox='0 0 24 24'
											fill='currentColor'
											width='24'
											height='24'
											style={{ fill: '#587587' }}
										>
											<path d='M12 2a1 1 0 011 1v1h4a2 2 0 012 2v3h1a1 1 0 110 2h-1v7a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-7H2a1 1 0 110-2h1V6a2 2 0 012-2h4V3a1 1 0 011-1zm-4 7a1 1 0 100 2 1 1 0 000-2zm8 0a1 1 0 100 2 1 1 0 000-2zm-8 4h8v5H8v-5z' />
										</svg>
										<h5
											className={styles['noMargin']}
										>{i18n`Trust AI Tittle`}</h5>
									</HStack>
									<h5>{i18n`Trust AI Suggestion`}</h5>
								</VStack>
								{isLoading ? (
									<p>Loading...</p>
								) : suggestions?.providerReferrals?.length ? (
									<>
										<h6
											className={styles['noMargin']}
										>{i18n`Trust AI Sections`}</h6>
										{suggestions.providerReferrals.map(
											(p, lineIndex) => {
												const profitSymbol =
													getProfitSymbol(p.profit)
												const distanceKm =
													p.distance != null
														? p.distance.toFixed(2)
														: '--'
												const days =
													p.averageTime != null
														? p.averageTime.toFixed(
																2
														  )
														: '--'
												return (
													<div
														key={lineIndex}
														style={{
															display: 'flex',
															justifyContent:
																'space-between',
															alignItems:
																'center',
															marginBottom:
																'10px',
														}}
													>
														<div
															style={{ flex: 1 }}
														>
															<p
																style={{
																	margin: 0,
																}}
															>
																{p.providerName}
															</p>
															<small
																style={{
																	display:
																		'flex',
																	alignItems:
																		'center',
																}}
															>
																{profitSymbol ? (
																	getSymbol(
																		profitSymbol.number,
																		profitSymbol.className
																	)
																) : (
																	<span
																		style={{
																			marginRight:
																				'5px',
																		}}
																	>
																		--
																	</span>
																)}
																<span>{`| ${distanceKm} km | ${days} d`}</span>
															</small>
														</div>
														<button
															type='button'
															onClick={() =>
																handleProviderClick(
																	p
																)
															}
															style={{
																backgroundColor:
																	selectedProviderId ===
																	p.providerId
																		? '#4caf50'
																		: '#ffffff',
																border:
																	selectedProviderId ===
																	p.providerId
																		? '1px solid #4caf50'
																		: '1px solid #e0e0e0',
																borderRadius:
																	'4px',
																cursor: 'pointer',
																fontSize:
																	'10px',
																fontWeight:
																	'500',
																padding:
																	'4px 8px',
																whiteSpace:
																	'nowrap',
																transition:
																	'all 0.15s ease',
															}}
															onMouseEnter={(
																e
															) => {
																if (
																	selectedProviderId !==
																	p.providerId
																) {
																	e.currentTarget.style.backgroundColor =
																		'#f0f0f0'
																	e.currentTarget.style.borderColor =
																		'#ccc'
																}
															}}
															onMouseLeave={(
																e
															) => {
																if (
																	selectedProviderId !==
																	p.providerId
																) {
																	e.currentTarget.style.backgroundColor =
																		'#ffffff'
																	e.currentTarget.style.borderColor =
																		'#e0e0e0'
																}
															}}
														>
															<span
																style={{
																	color:
																		selectedProviderId ===
																		p.providerId
																			? '#ffffff'
																			: '#555',
																}}
															>
																✓
															</span>
														</button>
													</div>
												)
											}
										)}
										{suggestions.description && (
											<p
												style={{
													marginTop: '10px',
													color: 'black',
													fontSize: '11px',
												}}
											>
												{suggestions.description}
											</p>
										)}
									</>
								) : (
									<p>{i18n`Erro ao tentar carregar os prestadores sugeridos`}</p>
								)}
							</fieldset>
						)}
					<fieldset>
						<h4>{i18n`Process forwarding`}</h4>
						<VStack className={styles['option-group-list']}>
							<VStack className={styles['form-input']}>
								<h5>{i18n`Provider`}</h5>
								<HFDropdown
									className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
									optionsAlign='right'
									searchable={true}
									name={providerFieldName}
									noData={i18n`No results`}
									options={filteredEstablishments || []}
									type='number'
									onChange={(e) =>
										handleManualProviderSelect(
											e?.target?.value
										)
									}
								/>
								<HFCheckbox
									className={`${styles['checkbox']} ${styles['provider-assist-check']}`}
									name='provider.alreadyBeingAssisted'
									label={i18n`Already being assisted at the selected provider`}
								/>
							</VStack>
						</VStack>
					</fieldset>
				</>
			) : null}

			{pendingProvider && (
				<>
					{showWarning && !selectedUserJustification && (
						<AlertBox
							className={styles['alert-box']}
							type='warning'
							items={[
								{
									title: i18n`Warning`,
									text: i18n`Justification is mandatory to proceed`,
								},
							]}
						/>
					)}
					<JustificationField
						justifications={justifications}
						providerName={pendingProvider.providerName}
						onJustificationChange={handleJustificationChange}
						onCancel={handleJustificationCancel}
					/>
				</>
			)}
		</>
	)
}
